import { ApiError } from '@api/api-client';
import {
  type AppUserDto,
  listAppUsers,
  resetUserPassword,
  updateAppUser,
} from '@api/app-users-api';
import Breadcrumbs from '@components/breadcrumbs';
import CountryPicker from '@components/country-picker';
import FieldError from '@components/field-error';
import Modal from '@components/modal';
import TableFilters from '@components/table-filters';
import AddUserModal from '@features/app-users/add-user-modal';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAuth } from '@hooks/use-auth';
import { useCountries } from '@hooks/use-countries';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type EditUserFormData, editUserSchema } from '@validation/schemas';
import {
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Copy,
  KeyRound,
  Pencil,
  RefreshCw,
  Shield,
  ShieldAlert,
  ShieldCheck,
  UserPlus,
  Users,
  XCircle,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { sileo } from 'sileo';

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;

const ROLE_CONFIG: Record<string, { label: string; icon: typeof Shield; className: string }> = {
  superadmin: { label: 'Superadmin', icon: ShieldAlert, className: 'badge badge-closed' },
  admin: { label: 'Admin', icon: ShieldCheck, className: 'badge badge-warning' },
  editor: { label: 'Editor', icon: Shield, className: 'badge badge-active' },
};

const EDITABLE_ROLES = [
  { value: 'admin', label: 'Admin' },
  { value: 'editor', label: 'Editor' },
] as const;

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function getPageRange(currentPage: number, totalPages: number): number[] {
  const MAX_VISIBLE = 7;

  if (totalPages <= MAX_VISIBLE) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages: number[] = [1];

  let start = Math.max(2, currentPage - 1);
  let end = Math.min(totalPages - 1, currentPage + 1);

  if (currentPage <= 3) {
    start = 2;
    end = 5;
  } else if (currentPage >= totalPages - 2) {
    start = totalPages - 4;
    end = totalPages - 1;
  }

  if (start > 2) pages.push(-1);
  for (let i = start; i <= end; i++) pages.push(i);
  if (end < totalPages - 1) pages.push(-2);

  pages.push(totalPages);
  return pages;
}

export default function ManageUsers(): React.JSX.Element {
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuth();
  const { countries } = useCountries();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showAddUser, setShowAddUser] = useState(false);
  const [editingUser, setEditingUser] = useState<AppUserDto | null>(null);
  const [resetPasswordUser, setResetPasswordUser] = useState<AppUserDto | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(search);
      setCurrentPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [search]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['app-users', currentPage, debouncedSearch],
    queryFn: () => listAppUsers(currentPage, PAGE_SIZE, debouncedSearch || undefined),
    staleTime: 30_000,
  });

  const users = data?.data ?? [];
  const totalItems = data?.meta?.totalItems ?? 0;
  const totalPages = data?.meta?.totalPages ?? 1;
  const startIndex = totalItems === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const endIndex = Math.min(currentPage * PAGE_SIZE, totalItems);

  const goToPage = useCallback((page: number | ((prev: number) => number)): void => {
    setCurrentPage(page);
  }, []);

  const refetch = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['app-users'] });
  }, [queryClient]);

  const getCountryName = useCallback(
    (code: string | null): string => {
      if (!code) return '—';
      const country = countries.find((c) => c.code === code);
      return country ? country.name : code;
    },
    [countries],
  );

  const getCountryFlag = useCallback(
    (code: string | null): string | null => {
      if (!code) return null;
      const country = countries.find((c) => c.code === code);
      return country ? `/flags/${country.locale}.svg` : null;
    },
    [countries],
  );

  return (
    <>
      <Breadcrumbs items={[{ label: 'Editions', to: '/editions' }, { label: 'Users' }]} />

      <div className="page-header">
        <div className="page-header__left">
          <h1 className="page-header__title">User Management</h1>
          <p className="page-header__subtitle">
            {totalItems} user{totalItems !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="page-header__actions">
          <button type="button" className="btn btn-secondary" onClick={refetch}>
            <RefreshCw size={16} />
            Refresh
          </button>
          <button type="button" className="btn btn-primary" onClick={() => setShowAddUser(true)}>
            <UserPlus size={16} />
            Add User
          </button>
        </div>
      </div>

      <TableFilters
        compact
        rows={[
          {
            items: [
              {
                type: 'search',
                id: 'user-search',
                label: 'Search',
                placeholder: 'Search by name, username or email...',
                value: search,
                onChange: setSearch,
                narrow: true,
              },
            ],
          },
        ]}
      />

      {isLoading && (
        <div className="empty-state">
          <p>Loading users...</p>
        </div>
      )}

      {error && (
        <div className="page-error" role="alert">
          <span>{error instanceof Error ? error.message : 'Failed to load users'}</span>
        </div>
      )}

      {!isLoading && !error && users.length === 0 && (
        <div className="empty-state">
          <Users size={40} strokeWidth={1} />
          <p>No users found.</p>
        </div>
      )}

      {!isLoading && !error && users.length > 0 && (
        <>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Email</th>
                  <th>Country</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Password</th>
                  <th>Created</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => {
                  const roleConfig = ROLE_CONFIG[user.role];
                  const RoleIcon = roleConfig?.icon ?? Shield;
                  const flag = getCountryFlag(user.country);

                  return (
                    <tr key={user.id}>
                      <td>
                        <div className="user-cell">
                          <span className="user-cell__name">{user.name}</span>
                          <span className="user-cell__username">@{user.username}</span>
                        </div>
                      </td>
                      <td>
                        <span className="text-sm">{user.email}</span>
                      </td>
                      <td>
                        {flag ? (
                          <span className="country-cell">
                            <img src={flag} alt="" className="country-cell__flag" />
                            {getCountryName(user.country)}
                          </span>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td>
                        <span className={roleConfig?.className ?? 'badge'}>
                          <RoleIcon size={12} />
                          {roleConfig?.label ?? user.role}
                        </span>
                      </td>
                      <td>
                        {user.isActive ? (
                          <span className="badge badge-active">
                            <CheckCircle size={12} />
                            Active
                          </span>
                        ) : (
                          <span className="badge badge-closed">
                            <XCircle size={12} />
                            Inactive
                          </span>
                        )}
                      </td>
                      <td>
                        {user.hasPassword ? (
                          <span className="password-status password-status--set">
                            <CheckCircle size={12} />
                            Set
                          </span>
                        ) : (
                          <span className="password-status">Not set</span>
                        )}
                      </td>
                      <td>
                        <span className="text-sm">{formatDate(user.createdAt)}</span>
                      </td>
                      <td>
                        <div className="table-actions">
                          {user.role !== 'superadmin' && (
                            <>
                              <button
                                type="button"
                                className="btn btn-ghost btn-icon btn-sm"
                                data-tooltip="Edit user"
                                aria-label="Edit user"
                                onClick={() => setEditingUser(user)}
                              >
                                <Pencil size={16} />
                              </button>
                              <button
                                type="button"
                                className="btn btn-ghost btn-icon btn-sm"
                                data-tooltip="Reset password"
                                aria-label="Reset password"
                                onClick={() => setResetPasswordUser(user)}
                              >
                                <KeyRound size={16} />
                              </button>
                            </>
                          )}
                          {user.role === 'superadmin' && user.id === currentUser?.id && (
                            <button
                              type="button"
                              className="btn btn-ghost btn-icon btn-sm"
                              data-tooltip="Reset password"
                              aria-label="Reset password"
                              onClick={() => setResetPasswordUser(user)}
                            >
                              <KeyRound size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <span className="pagination__info">
                {startIndex}&ndash;{endIndex} of {totalItems}
              </span>

              <div className="pagination__controls">
                <button
                  type="button"
                  className="pagination__btn"
                  disabled={currentPage === 1}
                  aria-label="Previous page"
                  onClick={() => goToPage((p) => p - 1)}
                >
                  <ChevronLeft size={16} />
                </button>

                {getPageRange(currentPage, totalPages).map((page) =>
                  page < 0 ? (
                    <span key={page} className="pagination__ellipsis">
                      &hellip;
                    </span>
                  ) : (
                    <button
                      key={page}
                      type="button"
                      className={`pagination__btn${page === currentPage ? ' pagination__btn--active' : ''}`}
                      onClick={() => goToPage(page)}
                    >
                      {page}
                    </button>
                  ),
                )}

                <button
                  type="button"
                  className="pagination__btn"
                  disabled={currentPage === totalPages}
                  aria-label="Next page"
                  onClick={() => goToPage((p) => p + 1)}
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {editingUser && (
        <EditUserModal
          user={editingUser}
          countries={countries}
          onClose={() => setEditingUser(null)}
          onSaved={() => {
            setEditingUser(null);
            refetch();
          }}
        />
      )}

      {resetPasswordUser && (
        <ResetPasswordModal
          user={resetPasswordUser}
          onClose={() => setResetPasswordUser(null)}
          // Refetch only: the modal stays open to show the generated password,
          // which is the one and only time it can be read.
          onSaved={refetch}
        />
      )}

      {showAddUser && (
        <AddUserModal
          onClose={() => setShowAddUser(false)}
          onCreated={() => {
            setShowAddUser(false);
            refetch();
          }}
        />
      )}
    </>
  );
}

interface EditUserModalProps {
  user: AppUserDto;
  countries: { id: string; code: string; name: string; locale: string }[];
  onClose: () => void;
  onSaved: () => void;
}

function EditUserModal({
  user,
  countries,
  onClose,
  onSaved,
}: EditUserModalProps): React.JSX.Element {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<EditUserFormData>({
    resolver: zodResolver(editUserSchema),
    defaultValues: {
      name: user.name,
      email: user.email,
      role: (user.role === 'admin' || user.role === 'editor' ? user.role : 'editor') as
        | 'admin'
        | 'editor',
      isActive: user.isActive,
      countryId: countries.find((c) => c.code === user.country)?.id ?? '',
    },
    mode: 'onBlur',
  });

  const onSubmit = async (data: EditUserFormData): Promise<void> => {
    setIsSubmitting(true);
    const selectedCountry = countries.find((c) => c.id === data.countryId);

    try {
      await updateAppUser(user.id, {
        name: data.name,
        email: data.email,
        role: data.role,
        isActive: data.isActive,
        country: selectedCountry?.code ?? null,
      });
      sileo.success({ title: 'User updated successfully' });
      onSaved();
    } catch (error) {
      if (error instanceof ApiError) {
        sileo.error({ title: error.message });
      } else {
        sileo.error({ title: 'Failed to update user' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={`Edit User — ${user.username}`}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            form="edit-user-form"
            className="btn btn-primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </button>
        </>
      }
    >
      <form id="edit-user-form" onSubmit={(e) => void handleSubmit(onSubmit)(e)}>
        <div className="modal-form">
          <div className="modal-form__row">
            <div className="form-group">
              <label htmlFor="edit-name" className="form-label">
                Name *
              </label>
              <input
                id="edit-name"
                type="text"
                className={`form-input${errors.name ? ' form-input--error' : ''}`}
                {...register('name')}
              />
              <FieldError error={errors.name} />
            </div>

            <div className="form-group">
              <label htmlFor="edit-email" className="form-label">
                Email *
              </label>
              <input
                id="edit-email"
                type="email"
                className={`form-input${errors.email ? ' form-input--error' : ''}`}
                {...register('email')}
              />
              <FieldError error={errors.email} />
            </div>
          </div>

          <div className="modal-form__row">
            <div className="form-group">
              <label htmlFor="edit-role" className="form-label">
                Role *
              </label>
              <select
                id="edit-role"
                className={`form-select${errors.role ? ' form-select--error' : ''}`}
                {...register('role')}
              >
                {EDITABLE_ROLES.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
              <FieldError error={errors.role} />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="edit-status-active">
                Status
              </label>
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <div className="status-toggle-group">
                    <button
                      id="edit-status-active"
                      type="button"
                      className={`btn btn-sm ${field.value ? 'btn-primary' : 'btn-secondary'}`}
                      onClick={() => field.onChange(true)}
                    >
                      <CheckCircle size={14} />
                      Active
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm ${!field.value ? 'btn-danger' : 'btn-secondary'}`}
                      onClick={() => field.onChange(false)}
                    >
                      <XCircle size={14} />
                      Inactive
                    </button>
                  </div>
                )}
              />
            </div>
          </div>

          <div className="form-group">
            <span className="form-label">Country</span>
            <Controller
              name="countryId"
              control={control}
              render={({ field }) => (
                <CountryPicker value={field.value ?? ''} onChange={field.onChange} />
              )}
            />
          </div>
        </div>
      </form>
    </Modal>
  );
}

interface ResetPasswordModalProps {
  user: AppUserDto;
  onClose: () => void;
  /**
   * Called once the reset succeeds, to refresh the list. It must NOT close this
   * modal: the generated password is shown afterwards and cannot be fetched again.
   */
  onSaved: () => void;
}

/**
 * Two steps: confirm, then read the generated password off the screen.
 *
 * Nobody types the new password any more. The reset generates one and returns it
 * exactly once — there is no way to fetch it again — so it is shown here for the
 * admin to pass to its owner, who is then required to replace it on next sign-in.
 */
function ResetPasswordModal({
  user,
  onClose,
  onSaved,
}: ResetPasswordModalProps): React.JSX.Element {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState<string | null>(null);
  const [hasCopied, setHasCopied] = useState(false);

  const handleReset = async (): Promise<void> => {
    setIsSubmitting(true);
    try {
      const { password } = await resetUserPassword(user.id);
      setGeneratedPassword(password);
      onSaved();
    } catch (error) {
      sileo.error({
        title: error instanceof ApiError ? error.message : 'Failed to reset password',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCopy = async (): Promise<void> => {
    if (!generatedPassword) return;
    try {
      await navigator.clipboard.writeText(generatedPassword);
      setHasCopied(true);
    } catch {
      sileo.error({ title: 'Could not copy. Select the password and copy it manually.' });
    }
  };

  if (generatedPassword) {
    return (
      <Modal
        isOpen
        onClose={onClose}
        title={`Password reset — ${user.name}`}
        footer={
          <button type="button" className="btn btn-primary" onClick={onClose}>
            Done
          </button>
        }
      >
        <div className="modal-form">
          <p className="form-hint form-hint--spaced">
            Send this password to <strong>{user.email}</strong>. It is shown only now — closing this
            dialog is the last time you will see it. {user.name} will be asked to choose their own
            password the next time they sign in.
          </p>

          <div className="form-group">
            <label htmlFor="generated-password" className="form-label">
              Generated password
            </label>
            <div className="password-input-wrapper">
              <input
                id="generated-password"
                type="text"
                className="form-input"
                value={generatedPassword}
                readOnly
                onFocus={(e) => e.currentTarget.select()}
              />
              <button
                type="button"
                className="btn-password-toggle"
                onClick={() => void handleCopy()}
                aria-label="Copy password"
              >
                {hasCopied ? <CheckCircle size={16} /> : <Copy size={16} />}
              </button>
            </div>
            {hasCopied && <span className="form-hint">Copied to the clipboard.</span>}
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={`Reset password — ${user.name}`}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={isSubmitting}
            onClick={() => void handleReset()}
          >
            <KeyRound size={16} />
            {isSubmitting ? 'Resetting...' : 'Generate new password'}
          </button>
        </>
      }
    >
      <div className="modal-form">
        <p className="form-hint form-hint--spaced">
          This replaces the password of <strong>@{user.username}</strong> with a generated one,
          shown once so you can send it on. Their current password stops working immediately, and
          they will have to choose a new one when they next sign in.
        </p>
      </div>
    </Modal>
  );
}
